import { TransferOrderKind } from "@redotech/cockroachdb-codegen/tables/inventory/transfer-order-kind";
import * as z from "zod";
import {
  parsePoCsvRows,
  PO_CSV_PRODUCT_COLUMNS,
  PoCsvProductRow,
} from "./po-csv-upload";

export const TRANSFER_CSV_HEADER_LABELS = {
  ORIGIN_LOCATION: "Origin location",
  DESTINATION_LOCATION: "Destination location",
  REFERENCE: "Reference",
  TAGS: "Tags",
  TRANSFER_TYPE: "Transfer type",
  NOTE: "Note",
} as const;

const LABELS = TRANSFER_CSV_HEADER_LABELS;
const COLS = PO_CSV_PRODUCT_COLUMNS;

const TagsSchema = z.preprocess(
  (value) => {
    if (typeof value !== "string") {
      return value;
    }
    const tags = value
      .split(",")
      .map((tag) => tag.trim())
      .filter(Boolean);
    return tags.length > 0 ? tags : undefined;
  },
  z.array(z.string().max(255)).optional(),
);

export const TransferCsvHeader = z.object({
  originLocationName: z
    .string()
    .min(1, `"${LABELS.ORIGIN_LOCATION}" is required`)
    .max(255),
  destinationLocationName: z
    .string()
    .min(1, `"${LABELS.DESTINATION_LOCATION}" is required`)
    .max(255),
  reference: z.string().max(255).optional(),
  tags: TagsSchema,
  kind: z
    .enum([TransferOrderKind.Simple, TransferOrderKind.Shipment])
    .default(TransferOrderKind.Simple),
  note: z.string().max(1000).optional(),
});
export type TransferCsvHeader = z.infer<typeof TransferCsvHeader>;

export const TransferCsvRowResult = z.object({
  status: z.enum(["created", "error"]),
  rowNumber: z.number().int().positive(),
  sku: z.string(),
  message: z.string().optional(),
  transferOrderId: z.string().optional(),
});
export type TransferCsvRowResult = z.infer<typeof TransferCsvRowResult>;

export const ImportTransferCsvFileResult = z.object({
  fileName: z.string(),
  totalCount: z.number().int().nonnegative(),
  createdCount: z.number().int().nonnegative(),
  errorCount: z.number().int().nonnegative(),
  results: z.array(TransferCsvRowResult),
  transferOrderIds: z.array(z.string()),
  parseErrors: z.array(z.string()),
});
export type ImportTransferCsvFileResult = z.infer<
  typeof ImportTransferCsvFileResult
>;

export const ImportTransferCsvBatchResult = z.object({
  files: z.array(ImportTransferCsvFileResult),
});
export type ImportTransferCsvBatchResult = z.infer<
  typeof ImportTransferCsvBatchResult
>;

export function parseTransferCsvRows(rows: readonly (readonly string[])[]): {
  header: Record<string, string>;
  productRows: ReturnType<typeof parsePoCsvRows>["productRows"];
  parseErrors: string[];
} {
  const parsedProducts = parsePoCsvRows(rows);
  const header: Record<string, string> = {};
  const labelsByLowerCase = new Map(
    Object.values(LABELS).map((label) => [label.toLowerCase(), label]),
  );

  for (const row of rows) {
    const label = labelsByLowerCase.get((row[0] ?? "").trim().toLowerCase());
    if (label) {
      header[label] = (row[1] ?? "").trim();
    }
  }

  return { ...parsedProducts, header };
}

export function parseTransferCsvHeader(header: Record<string, string>) {
  return TransferCsvHeader.safeParse({
    originLocationName: header[LABELS.ORIGIN_LOCATION] ?? "",
    destinationLocationName: header[LABELS.DESTINATION_LOCATION] ?? "",
    reference: header[LABELS.REFERENCE] || undefined,
    tags: header[LABELS.TAGS] || undefined,
    kind: header[LABELS.TRANSFER_TYPE] || undefined,
    note: header[LABELS.NOTE] || undefined,
  });
}

export { PoCsvProductRow as TransferCsvProductRow };

export const TRANSFER_CSV_TEMPLATE_LINES: readonly (readonly string[])[] = [
  [LABELS.ORIGIN_LOCATION, "Main warehouse"],
  [LABELS.DESTINATION_LOCATION, "Retail store"],
  [LABELS.REFERENCE, ""],
  [LABELS.TAGS, ""],
  [LABELS.TRANSFER_TYPE, TransferOrderKind.Simple],
  [LABELS.NOTE, ""],
  [],
  [COLS.SKU, COLS.PRODUCT_TITLE, COLS.QUANTITY],
  ["ABC-100", "", "100"],
  ["ABC-200", "", "50"],
];
